import { useEffect } from 'react';
import { BRANDING_ASSET_PATHS } from '@command-center/core-platform';
import type { ResolvedBranding } from './types';

interface PortalMetadataProps {
  branding: ResolvedBranding;
}

type IconLinkKind = 'favicon' | 'apple-touch-icon';

/**
 * Keeps browser icon metadata on the same branding convention as the shell.
 * Missing icon.png assets fall back to logo.png and finally to a generated
 * text mark, so adopting the convention does not break existing portals.
 */
export function PortalMetadata({ branding }: PortalMetadataProps) {
  useEffect(() => {
    const candidates = [...new Set([
      branding.iconSrc ?? BRANDING_ASSET_PATHS.icon,
      branding.logoSrc ?? BRANDING_ASSET_PATHS.logo,
    ].filter(Boolean))];
    let cancelled = false;
    let loader: HTMLImageElement | undefined;

    const publish = (href: string, type?: string) => {
      if (cancelled) return;
      updateIconLink('favicon', href, type);
      updateIconLink('apple-touch-icon', href);
    };

    const tryCandidate = (index: number) => {
      const candidate = candidates[index];
      if (!candidate) {
        publish(generatedIconDataUrl(branding), 'image/svg+xml');
        return;
      }

      publish(candidate, 'image/png');
      loader = new window.Image();
      loader.onload = () => publish(candidate, 'image/png');
      loader.onerror = () => tryCandidate(index + 1);
      loader.src = candidate;
    };

    tryCandidate(0);
    return () => {
      cancelled = true;
      if (loader) {
        loader.onload = null;
        loader.onerror = null;
      }
    };
  }, [branding.iconSrc, branding.logoSrc, branding.iconText, branding.colors?.primary]);

  return null;
}

function updateIconLink(kind: IconLinkKind, href: string, type?: string): void {
  const selector = `link[data-portal-icon="${kind}"]`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.dataset.portalIcon = kind;
    link.rel = kind === 'favicon' ? 'icon' : 'apple-touch-icon';
    document.head.appendChild(link);
  }
  link.href = href;
  if (type) link.type = type;
  else link.removeAttribute('type');
}

function generatedIconDataUrl(branding: ResolvedBranding): string {
  const color = /^#[0-9a-f]{6}$/i.test(branding.colors?.primary ?? '')
    ? branding.colors!.primary!
    : '#79141a';
  const text = escapeXml(branding.iconText.slice(0, 3));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="13" fill="${color}"/><text x="32" y="34" fill="white" font-family="Arial,sans-serif" font-size="22" font-weight="700" text-anchor="middle" dominant-baseline="middle">${text}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
